import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import {
  FeatureInfo,
  PermissionCatalog,
  PermissionInfo,
  SubFeatureInfo,
} from "@/types/team"
import { IconCheck, IconChevronRight, IconMinus } from "@tabler/icons-react"
import { useMemo } from "react"

interface PermissionMatrixProps {
  catalog: PermissionCatalog
  value: string[]
  onChange: (next: string[]) => void
  disabled?: boolean
}

function keysOf(group: FeatureInfo | SubFeatureInfo): string[] {
  const direct = group.permissions.map((p) => p.key)
  const nested =
    "subFeatures" in group
      ? group.subFeatures.flatMap((s) => s.permissions.map((p) => p.key))
      : []
  return [...direct, ...nested]
}

function groupState(
  keys: string[],
  selected: Set<string>,
): boolean | "indeterminate" {
  const on = keys.filter((k) => selected.has(k)).length
  if (on === 0) return false
  if (on === keys.length) return true
  return "indeterminate"
}

// Only destructive / privileged actions get the red marker; reads and ordinary
// writes stay muted so the eye lands on the dangerous grants.
const KIND_CLASS: Record<string, string> = {
  delete: "text-destructive",
  admin: "text-destructive",
}

export function PermissionMatrix({
  catalog,
  value,
  onChange,
  disabled = false,
}: PermissionMatrixProps) {
  const { translate } = useTranslation()
  const selected = useMemo(() => new Set(value), [value])

  const setMany = (keys: string[], on: boolean) => {
    const next = new Set(selected)
    keys.forEach((k) => (on ? next.add(k) : next.delete(k)))
    onChange([...next])
  }
  const toggleOne = (key: string) => setMany([key], !selected.has(key))
  const applyPreset = (keys: string[]) => onChange([...new Set(keys)])

  // Tri-state bulk toggle for a whole feature / sub-feature. Renders a proper
  // dash when partially granted instead of a misleading full check.
  const GroupHeader = ({
    keys,
    label,
    bold = true,
  }: {
    keys: string[]
    label: string
    bold?: boolean
  }) => {
    const state = groupState(keys, selected)
    const count = keys.filter((k) => selected.has(k)).length
    const active = state === true || state === "indeterminate"
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => setMany(keys, state !== true)}
        className="flex w-full items-center gap-2.5 text-left disabled:cursor-not-allowed"
        aria-pressed={state === true}
      >
        <span
          className={cn(
            "grid h-4 w-4 shrink-0 place-content-center rounded-[4px] border transition-colors",
            active
              ? "border-primary bg-primary text-primary-foreground"
              : "border-input bg-transparent",
            disabled && "opacity-50",
          )}
        >
          {state === true ? (
            <IconCheck size={12} stroke={3} />
          ) : state === "indeterminate" ? (
            <IconMinus size={12} stroke={3} />
          ) : null}
        </span>
        <span className={cn("text-sm", bold ? "font-semibold" : "font-medium")}>
          {label}
        </span>
        <span
          className={cn(
            "ml-auto text-xs tabular-nums",
            count > 0 ? "text-muted-foreground" : "text-muted-foreground/60",
          )}
        >
          {count}/{keys.length}
        </span>
      </button>
    )
  }

  const Actions = ({ perms }: { perms: PermissionInfo[] }) => (
    <div className="mt-2.5 grid grid-cols-1 gap-x-6 gap-y-2.5 pl-[26px] sm:grid-cols-2">
      {perms.map((p) => (
        <label
          key={p.key}
          className={cn(
            "flex items-center gap-2.5 text-sm transition-colors",
            disabled ? "cursor-not-allowed" : "cursor-pointer",
            selected.has(p.key) ? "text-foreground" : "text-muted-foreground",
          )}
        >
          <Checkbox
            checked={selected.has(p.key)}
            disabled={disabled}
            onCheckedChange={() => toggleOne(p.key)}
            className="border-input data-[state=checked]:border-primary"
          />
          <span className="flex-1">{p.label}</span>
          {p.status === "new" ? (
            <span className="rounded-md bg-primary/15 px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-wider text-primary">
              {translate("TEAM/PERMISSION_NEW_BADGE")}
            </span>
          ) : null}
          <span
            className={cn(
              "text-[11px] font-medium uppercase tracking-wide text-muted-foreground",
              KIND_CLASS[p.kind],
            )}
          >
            {p.kind}
          </span>
        </label>
      ))}
    </div>
  )

  const totalKeys = useMemo(
    () => catalog.features.flatMap((f) => keysOf(f)),
    [catalog],
  )
  const grantedCount = totalKeys.filter((k) => selected.has(k)).length

  return (
    <div className="space-y-3">
      {/* Presets + live granted counter */}
      <div className="flex flex-wrap items-center gap-2.5">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {translate("TEAM/PRESET_LABEL")}
        </span>
        {catalog.presets.map((p) => (
          <Button
            key={p.key}
            type="button"
            size="sm"
            variant="outline"
            disabled={disabled}
            className="h-7 rounded-full px-3 text-xs font-medium"
            onClick={() => applyPreset(p.permissions)}
          >
            {p.label}
          </Button>
        ))}
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={disabled}
          className="h-7 rounded-full px-3 text-xs text-muted-foreground"
          onClick={() => applyPreset([])}
        >
          {translate("TEAM/PRESET_CLEAR")}
        </Button>
        <span className="ml-auto rounded-full bg-muted px-3 py-1 text-xs font-medium tabular-nums text-foreground">
          {translate("TEAM/ACTIONS_GRANTED", { count: grantedCount })}
        </span>
      </div>

      {/* Feature groups */}
      <div className="divide-y divide-border overflow-hidden rounded-xl border">
        {catalog.features.map((feature) => {
          const fKeys = keysOf(feature)
          const isModule = feature.subFeatures.length > 0
          if (!isModule) {
            return (
              <div key={feature.key} className="px-4 py-3.5">
                <GroupHeader keys={fKeys} label={feature.label} />
                <Actions perms={feature.permissions} />
              </div>
            )
          }
          return (
            <Collapsible key={feature.key}>
              <div className="flex items-center gap-2 bg-muted/40 px-4 py-3">
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="shrink-0 text-muted-foreground transition-colors hover:text-foreground [&[data-state=open]>svg]:rotate-90"
                    aria-label={feature.label}
                  >
                    <IconChevronRight
                      size={15}
                      className="transition-transform"
                    />
                  </button>
                </CollapsibleTrigger>
                <div className="flex-1">
                  <GroupHeader keys={fKeys} label={feature.label} />
                </div>
              </div>
              <CollapsibleContent>
                <div className="space-y-3 border-l-2 border-primary/30 px-4 py-3">
                  {feature.subFeatures.map((sub) => (
                    <div key={sub.key}>
                      <GroupHeader
                        keys={keysOf(sub)}
                        label={sub.label}
                        bold={false}
                      />
                      <Actions perms={sub.permissions} />
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )
        })}
      </div>
    </div>
  )
}
